import { useEffect, useState } from 'preact/hooks';
import { getCoachStudent, type CoachHistoryEntryApi } from '../lib/api';

// CoachStudent — one student's history, at `#/pro/students/:label` (rung 3).
//
// Spec: `spec-the-outside-professional-surface-coaches-and-agents-2026-08-26` §8.2
//
// ⚑ THIS SCREEN IS THE PROOF OF RULING 4. Huan: "when the coach loses access
// to the actual video they don't lose access to the historical record." It
// reads `coach_review_log` and NOTHING ELSE — no grant, no household record —
// so it keeps working after every link has lapsed and after a family has left
// the product entirely. A version of this assembled from live grants would
// look identical on the day it was built and would empty itself, silently,
// over the following months.
//
// ⚠ NEWEST FIRST here, unlike the queue. The queue is an obligation list, so
// it takes the far-side instrument (soonest deadline first); a history is a
// record of what happened, and the most recent note is the one a coach opens
// it for. Two lists, two orders, both deliberate.

export interface CoachStudentProps {
  /** The performer label, as it was RECORDED at submit time. Never re-derived
   * — the record it came from may be gone, which is the whole point. */
  label: string;
}

type Phase =
  | { kind: 'loading' }
  | { kind: 'ready'; student: string; entries: CoachHistoryEntryApi[] }
  | { kind: 'error'; message: string };

function whenReadable(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const NOTES_FRAME = 'We couldn’t load these notes.';

export function CoachStudent({ label }: CoachStudentProps) {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    getCoachStudent(label)
      .then((r) => {
        if (cancelled) return;
        setPhase({ kind: 'ready', student: r.student, entries: r.entries });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Loud, never an empty page. A history that cannot load and a student
        // with no notes yet must not read the same — this surface's whole
        // failure mode is a list that says nothing when it should say
        // something.
        setPhase({
          kind: 'error',
          message: err instanceof Error ? err.message : NOTES_FRAME,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [label]);

  if (phase.kind === 'loading') {
    return <p class="coach__quiet">Looking up your notes…</p>;
  }

  if (phase.kind === 'error') {
    // The frame once, the server's sentence only when it adds one.
    return (
      <p class="coach__quiet">
        {phase.message === NOTES_FRAME ? NOTES_FRAME : `${NOTES_FRAME} ${phase.message}`}
      </p>
    );
  }

  return (
    <div class="coach-student">
      <h1 class="coach__title">{phase.student}</h1>
      {phase.entries.length === 0 ? (
        <p class="coach__quiet">No notes yet for this student.</p>
      ) : (
        <ul class="coach-student__list" aria-label={`Notes for ${phase.student}`}>
          {phase.entries.map((e) => (
            <li key={e.id} class="coach-student__entry">
              <div class="coach-student__head">
                <span class="coach-student__what">
                  {/* Role and project as the coach saw them at the time. Either
                      may be missing; neither is re-derived. */}
                  {[e.role, e.project].filter(Boolean).join(' · ') || 'A tape'}
                </span>
                <span class="coach-student__when">{whenReadable(e.reviewedAt)}</span>
              </div>
              {/* The coach's OWN words, shown whole. Not summarised, not
                  truncated — this is the thing they came back for. */}
              <p class="coach-student__notes">{e.notes}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
